/**
 * RoomInviteComposeCard — inline f8 card type `mediation_invite_compose`.
 *
 * Rendered inline in the f8 chat (NOT its own page) when the user asks f8 to
 * set up a mediation. Mirrors the app's `RoomInviteComposeCard.js`
 * field-for-field — the card shape is shared so the same skill payload can
 * surface on either platform.
 *
 * Layout: one-rail InlineShell hosting:
 *   - reason textarea (≤1000 chars, matches MAX_REASON_LEN in the model)
 *   - "ask f8 to help phrase" helper that fires a skill_invoke message
 *   - four-channel picker: in-app / email / sms / link
 *   - send button → createMediationInviteEurW via cardActionHandler
 *
 * ── SHARED CARD SHAPE (web ↔ app) ──────────────────────────────────────────
 *   {
 *     type: 'mediation_invite_compose',
 *     data: {
 *       inviteeId:    string | null, // the invitee's uid when resolved
 *       inviteeName:  string | null, // display name only; never load-bearing
 *       inviteeEmail: string | null, // PPL-131 — email invitee; only ever set
 *                                    // when inviteeId is null (resolved user
 *                                    // wins). Editable here; sent to the
 *                                    // callable instead of inviteeId.
 *       reasonPrefill: string?,      // optional f8-suggested reason
 *       phrasingSuggestions?: string[], // ≤3 short alternative phrasings
 *     }
 *   }
 */

import React, { useState } from 'react';
import { Send, Check, Mail, MessageCircle, Smartphone, Link2, type Icon as FeatherIcon } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  type F8CardData,
  type OnAction,
} from './cardHelpers';

const CHANNELS: { id: 'in_app' | 'email' | 'sms' | 'link'; label: string; Icon: FeatherIcon }[] = [
  { id: 'in_app', label: 'in-app', Icon: MessageCircle },
  { id: 'email', label: 'email', Icon: Mail },
  { id: 'sms', label: 'sms', Icon: Smartphone },
  { id: 'link', label: 'link', Icon: Link2 },
];

const MAX_REASON_LEN = 1000;
const MAX_EMAIL_LEN = 254;
// Shape check only — createMediationInviteEurW does the authoritative
// validation. Same pattern RoomList.tsx uses for the same send path (PPL-60).
const EMAIL_RE = /^\S+@\S+\.\S+$/;

interface ComposeData {
  inviteeId?: string | null;
  inviteeName?: string | null;
  inviteeEmail?: string | null;
  reasonPrefill?: string;
  phrasingSuggestions?: string[];
}

export default function RoomInviteComposeCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const data = (card?.data || {}) as ComposeData;
  const inviteeId = data.inviteeId;
  const inviteeName = data.inviteeName;
  // PPL-131 — email invitee. Only ever set when inviteeId is null (the skill
  // handler drops the email once the person resolves to a pplkpr user).
  const inviteeEmailPrefill = typeof data.inviteeEmail === 'string' ? data.inviteeEmail : '';
  const reasonPrefill = typeof data.reasonPrefill === 'string' ? data.reasonPrefill : '';
  const phrasingSuggestions = Array.isArray(data.phrasingSuggestions)
    ? data.phrasingSuggestions.slice(0, 3)
    : [];

  const [reason, setReason] = useState<string>(reasonPrefill);
  const [inviteeEmail, setInviteeEmail] = useState<string>(inviteeEmailPrefill);
  const [channel, setChannel] = useState<'in_app' | 'email' | 'sms' | 'link'>('in_app');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedReason = reason.trim();
  const trimmedEmail = inviteeEmail.trim().toLowerCase();
  const hasValidEmail = !inviteeId && EMAIL_RE.test(trimmedEmail);
  const canSend = (!!inviteeId || hasValidEmail) && trimmedReason.length > 0 && !sending && !sent;

  // Who the invite is addressed to, for the eyebrow + sent state. Name and
  // email can coexist (the user named someone f8 couldn't resolve, plus an
  // email) — show both; the email is what actually gets sent.
  const recipientLabel = inviteeName && hasValidEmail
    ? `${inviteeName} (${trimmedEmail})`
    : inviteeName || (hasValidEmail ? trimmedEmail : null);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const result = await onAction?.({
        action: 'cloud_function',
        params: {
          functionName: 'createMediationInviteEurW',
          // inviteeName rides along so the pending row on /rooms (and the
          // placeholder invite email) can greet by name instead of "them".
          args: inviteeId
            ? { inviteeId, ...(inviteeName ? { inviteeName } : {}), reason: trimmedReason, channel, source: 'f8_propose_session' }
            : { inviteeEmail: trimmedEmail, ...(inviteeName ? { inviteeName } : {}), reason: trimmedReason, channel, source: 'f8_propose_session' },
        },
      });
      // Inspect the callable response: if success is explicitly false, surface the
      // reason rather than marking the card as sent. Only mark sent on a clean success
      // or when the result shape isn't a success:false response.
      const data = result && result.type === 'cloud_function' ? result.data : null;
      if (data && data.success === false) {
        setError((data.reason as string | undefined) || 'something went wrong — please try again');
      } else if (data && data.created === false && data.resent === true) {
        // Pending invitation — the server re-delivered the invite email.
        setSent(true);
      } else if (data && data.created === false) {
        // Existing active room with this person — the server sent nothing
        // (or the hourly resend throttle applied).
        setError(data.resendThrottled
          ? 'invitation already re-sent recently — give them a little time'
          : 'you already have an open room with them — find it in your rooms');
      } else {
        setSent(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'could not send invite';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleAskF8 = () => {
    const handle = inviteeName ? `@${inviteeName}` : 'them';
    onAction?.({
      action: 'skill_invoke',
      params: {
        message: `help me phrase what i want to say to ${handle} about mediating. my current draft is: "${trimmedReason || '(empty)'}"`,
      },
    });
  };

  if (sent) {
    return (
      <InlineShell>
        <InlineEyebrow
          label="invite sent"
          trailing={recipientLabel ? `to ${recipientLabel}` : undefined}
        />
        <div className="f8c-confirm-row">
          <div className="f8c-confirm-icon">
            <Check size={16} />
          </div>
          <div className="f8c-confirm-body">
            <div className="f8c-confirm-title">
              {recipientLabel ? `${recipientLabel} will see your invitation.` : 'they will see your invitation.'}
            </div>
            <div className="f8c-confirm-summary">channel: {channel.replace('_', '-')}</div>
          </div>
          <CardButton
            small
            onClick={() =>
              onAction?.({
                action: 'navigate',
                params: { screen: 'Messages' },
              })
            }
          >
            open
          </CardButton>
        </div>
      </InlineShell>
    );
  }

  return (
    <InlineShell>
      <InlineEyebrow
        label="open a room"
        trailing={recipientLabel ? `with ${recipientLabel}` : undefined}
      />

      {/* Reason textarea */}
      <div className="f8c-medinv-label">your reason</div>
      <div className="f8c-medinv-textarea-wrap">
        <textarea
          className="f8c-medinv-textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LEN))}
          placeholder="short, text-only. they'll see this first."
          maxLength={MAX_REASON_LEN}
          rows={3}
          disabled={sending}
        />
        <span className="f8c-medinv-counter">
          {reason.length}/{MAX_REASON_LEN}
        </span>
      </div>

      {/* Help-me-phrase helper + optional suggestion chips */}
      <div className="f8c-medinv-phrasing-row">
        <CardButton small onClick={handleAskF8} disabled={sending}>
          ask f8 to help phrase
        </CardButton>
      </div>
      {phrasingSuggestions.length > 0 && (
        <div className="f8c-medinv-suggestions">
          {phrasingSuggestions.map((s, i) => (
            <button
              key={`sug-${i}`}
              type="button"
              className="f8c-medinv-suggestion"
              onClick={() => setReason(s)}
              disabled={sending}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Channel picker — four equal-weight options */}
      <div className="f8c-medinv-label f8c-medinv-label-channel">send via</div>
      <div className="f8c-medinv-channels">
        {CHANNELS.map((c) => {
          const Icon = c.Icon;
          const active = channel === c.id;
          return (
            <button
              key={c.id}
              type="button"
              className={`f8c-medinv-channel${active ? ' active' : ''}`}
              onClick={() => setChannel(c.id)}
              disabled={sending}
            >
              <Icon size={13} />
              <span>{c.label}</span>
            </button>
          );
        })}
      </div>

      {/* Email invitee (PPL-131) — editable whenever the recipient isn't a
          resolved pplkpr user (matches the app card), so an unresolved name
          without an email isn't a dead end. The invite goes out to this
          address. */}
      {!inviteeId && (
        <>
          <div className="f8c-medinv-label f8c-medinv-label-channel">their email</div>
          <input
            type="email"
            className="f8c-medinv-email-input"
            value={inviteeEmail}
            onChange={(e) => setInviteeEmail(e.target.value.slice(0, MAX_EMAIL_LEN))}
            placeholder="name@example.com"
            maxLength={MAX_EMAIL_LEN}
            disabled={sending}
          />
          {!hasValidEmail && (
            <div className="f8c-medinv-missing-hint">
              {trimmedEmail.length > 0
                ? "that doesn't look like a valid email address"
                : 'enter their email address, or tell f8 their name or @handle'}
            </div>
          )}
        </>
      )}

      {/* Send + error */}
      <div className="f8c-medinv-send-row">
        <CardButton primary small onClick={handleSend} disabled={!canSend}>
          <Send size={12} />
          {sending ? 'sending…' : 'send invite'}
        </CardButton>
        {!!error && <span className="f8c-medinv-error">{error}</span>}
      </div>
    </InlineShell>
  );
}
